<template>
  <ion-header>
    <ion-toolbar :style="contentStyles">
      <ion-buttons slot="start">
        <ion-back-button />
      </ion-buttons>
      <ion-title>{{ $t('order.promotions') }}</ion-title>
    </ion-toolbar>
  </ion-header>
  <ion-content :fullscreen="true" :style="contentStyles">
    <AppStack
      v-if="loading"
      direction="flex-col"
      flex="flex-1"
      align-items="center"
      justify-content="center"
      class="py-10 w-full absolute h-full left-0 top-0"
    >
      <AppSpinner
        class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
      />
    </AppStack>
    <template v-else>
      <AppStack direction="flex-col" flex="flex-1" class="py-6 gap-8">
        <AppStack
          v-if="addedPromotionsList.length"
          direction="flex-col"
          class="gap-3"
        >
          <AppTypography
            :text="$t('order.added')"
            name="text-md-4-medium"
            color="text-fg-elevation-2-secondary"
            class="px-5"
          />
          <AppStack direction="flex-col">
            <template
              v-for="promotion in addedPromotionsList"
              :key="promotion._id"
            >
              <OrderModalPromotionsItem
                :title="promotion.title"
                :disabled="orderUpdateInProgress || !canApplyPromotions"
                :description="promotion.description"
                :applied="
                  promotion._id in (order?.appliedPromotions?.promotions || {})
                "
                removable
                @remove="onAppliedPromotionRemove(promotion._id)"
              />
              <div class="pl-5">
                <div class="h-[0.5px] bg-border-elevation-2-secondary" />
              </div>
            </template>
          </AppStack>
        </AppStack>
        <AppStack direction="flex-col" class="gap-3">
          <AppTypography
            :text="$t('promotions.matchingPromotions')"
            name="text-md-4-medium"
            color="text-fg-elevation-2-secondary"
            class="px-5"
          />
          <AppStack direction="flex-col">
            <template v-for="promotion in promotionsList" :key="promotion._id">
              <OrderModalPromotionsItem
                :title="promotion.name"
                :disabled="orderUpdateInProgress || !canApplyPromotions"
                :description="promotion.description"
                :applied="
                  promotion._id in (order?.appliedPromotions?.promotions || {})
                "
                @apply="onPromotionApply(promotion._id)"
              />
              <div class="pl-5">
                <div class="h-[0.5px] bg-border-elevation-2-secondary" />
              </div>
            </template>
          </AppStack>
        </AppStack>
      </AppStack>
      <div
        :style="{
          height: `${addedPromotionsList.length * 18 + 154 + 90}px`,
        }"
      />
      <AppStack
        direction="flex-col"
        class="fixed bottom-0 left-0 w-full safe-bottom-max px-5 bg-bg-elevation-2"
      >
        <div
          class="absolute top-[-90px] left-0 w-full h-[90px] pointer-events-none bg-gradient-to-b
            to-bg-elevation-2 from-transparent"
        />
        <AppStack direction="flex-col" class="py-5 gap-3">
          <AppStack align-items="items-baseline">
            <AppTypography
              name="text-xl-7-semibold"
              color="text-fg-elevation-2-primary"
              :text="formatPrice(orderTotal)"
            />
            <AppTypography
              v-if="orderSecondaryTotal"
              name="text-lg-7-semibold"
              color="text-fg-elevation-2-secondary"
              class="ml-3 line-through"
              :text="formatPrice(orderSecondaryTotal)"
            />
          </AppStack>
          <template v-if="addedPromotionsList.length">
            <AppStack
              v-for="promotion in addedPromotionsList"
              :key="promotion._id"
              justify-content="justify-between"
            >
              <AppTypography
                name="text-md-4-medium"
                color="text-fg-elevation-2-secondary"
                :text="promotion.title"
              />
              <AppTypography
                name="text-md-4-medium"
                color="text-fg-elevation-2-secondary"
                :text="promotion.discount"
              />
            </AppStack>
          </template>
        </AppStack>
        <AppButton
          variant="secondary"
          :loading="orderUpdateInProgress"
          :text="$t('promotions.done')"
          @click="pop()"
        />
      </AppStack>
    </template>
  </ion-content>
</template>

<script lang="ts">
export default { name: 'OrderModalPromotions' }
</script>

<script setup lang="ts">
import {
  IonButtons,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonBackButton,
} from '@ionic/vue'
import { ref, computed, watch, inject, ComputedRef } from 'vue'
import AppStack from '@restify/packages/design-system/low-level/AppStack.vue'
import AppSpinner from '@restify/packages/design-system/low-level/AppSpinner.vue'
import AppTypography from '@restify/packages/design-system/low-level/AppTypography.vue'
import { useIonicNavigation } from '@restify/packages/composables/useIonicNavigation'
import AppButton from '~/components/AppButton.vue'
import OrderModalPromotionsItem from './OrderModalPromotionsItem.vue'
import useAppHelpers from '~/composables/useAppHelpers'
import useStores from '~/composables/useStores'
import { useAuthStore } from '~/stores/auth'

const { pop } = useIonicNavigation('order-modal-nav')
const { orders: OrdersStore, promotions: PromotionsStore } = useStores()
const { formatPrice } = useAppHelpers()
const AuthStore = useAuthStore()

// Feature flag `VITE_FEATURE_MANAGER_PROMOTIONS` (off by default).
// When set, waiters can browse applied/available promotions but the
// apply/remove buttons are disabled — same rule as station-app +
// backoffice, mirrored server-side by `requireManagerRole` on
// orders.applyPromotion/removePromotion.
const managerOnlyPromotions =
  import.meta.env.VITE_FEATURE_MANAGER_PROMOTIONS === '1' ||
  import.meta.env.VITE_FEATURE_MANAGER_PROMOTIONS === 'true'

const canApplyPromotions = computed(() => {
  if (!managerOnlyPromotions) return true
  const role = AuthStore.reactiveUser?.profile?.role
  const permType = AuthStore.reactiveUser?.permissions?.type
  return (
    role === 'manager' ||
    permType === 'manager' ||
    permType === 'app-owner' ||
    permType === 'superadmin'
  )
})

const injectedIsOpen = inject<ComputedRef<boolean>>('isOpen')
const injectedOrderId = inject<ComputedRef<string>>('orderId')

const loading = ref(false)
const orderUpdateInProgress = ref(false)

const contentStyles = computed(() => ({
  '--background': 'var(--rf-bg-elevation-2)',
  '--border-color': 'var(--rf-border-elevation-2-primary)',
}))

const order = computed(() => {
  if (!injectedOrderId?.value) return null

  return OrdersStore.getFromStore(injectedOrderId.value).value
})

const orderTotal = computed(() => {
  return typeof order.value?.appliedPromotions?.total === 'number'
    ? order.value?.appliedPromotions?.total || 0
    : order.value?.total || 0
})

const orderSecondaryTotal = computed(() => {
  return typeof order.value?.appliedPromotions?.total === 'number'
    ? order.value?.total || 0
    : null
})

const addedPromotionsList = computed(() => {
  const ids = [...Object.keys(order.value?.appliedPromotions?.promotions || {})]

  if (!ids.length) return []

  return PromotionsStore.findInStore({
    _id: { $in: ids },
  })
    .map((promotion) => promotion.value)
    .filter((promotion) => promotion !== undefined)
    .map((promotion) => {
      const discount =
        order.value?.appliedPromotions?.promotions?.[
          promotion._id
        ]?.discount.toFixed(2)

      return {
        _id: promotion._id,
        title: promotion.name,
        description: formatPrice(discount ? -discount : 0),
        discount: formatPrice(
          order.value?.appliedPromotions?.promotions?.[promotion._id]?.discount
            ? -order.value?.appliedPromotions?.promotions?.[promotion._id]
                ?.discount
            : 0,
        ),
      }
    })
})

const promotionsList = computed(() => {
  if (!order.value) return []

  return PromotionsStore.findInStore({
    orderType: order.value.type,
    status: 'public',
  })
    .map((item) => item.value)
    .filter((promotion) => promotion !== undefined)
})

const onPromotionApply = (id: string) => {
  if (!order.value?._id) return

  orderUpdateInProgress.value = true

  // Check if promotion already exists in appliedPromotions
  const exists = order.value.appliedPromotions?.promotions?.[id]

  if (!exists) {
    return OrdersStore.applyPromotion(null, {
      orderId: order.value._id,
      promotionId: id,
    }).finally(() => {
      orderUpdateInProgress.value = false
    })
  }

  orderUpdateInProgress.value = false
}

const onAppliedPromotionRemove = (
  id: (typeof PromotionsStore)['Result']['_id'],
) => {
  if (!order.value) return

  orderUpdateInProgress.value = true

  return OrdersStore.removePromotion(null, {
    orderId: order.value._id,
    promotionId: id,
  })
    .catch(console.log)
    .finally(() => {
      orderUpdateInProgress.value = false
    })
}

const getPromotions = () => {
  // Get promotion IDs from appliedPromotions.promotions only
  const ids = Object.keys(order?.value?.appliedPromotions?.promotions || {})

  if (!order.value) return

  return Promise.all([
    PromotionsStore.find({
      query: {
        _id: { $in: ids },
        $limit: 100,
      },
    }),
    PromotionsStore.find({
      query: {
        orderType: order.value.type,
        status: 'public',
        startDate: { $lt: new Date().getTime() },
        endDate: { $gt: new Date().getTime() },
        $limit: 100,
      },
    }),
  ])
}

const getOrder = () => {
  if (injectedOrderId?.value === 'new' || !injectedOrderId?.value)
    return Promise.resolve()

  return OrdersStore.get(injectedOrderId.value)
}

const fetchData = () => {
  loading.value = true

  return getOrder()
    .then(() => Promise.all([getPromotions()]))
    .finally(() => (loading.value = false))
}

watch(
  computed(() => injectedIsOpen?.value),
  (newVal) => newVal && fetchData(),
  { immediate: true },
)
</script>
